import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { bigOf, fmtInt, type LedgerTotals } from '../engine';
import { useScrollRamp } from '../motion/useScrollRamp';
import { SEGMENT_COLORS } from './chartTokens';

/**
 * «Unaccounted»: la parte de lo que salió del juego que no tiene destino en
 * el libro. Cada unidad que dejó una estación tiene que estar en un segmento;
 * ésta es la que no lo está.
 */
export function UnaccountedCard({ totals, className }: { totals: LedgerTotals; className?: string }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const pending = totals.consumed === 0;
  const split = [
    { key: 'delivered', label: t('dash.unacc.delivered'), v: totals.delivered },
    { key: 'pending', label: t('dash.unacc.pending'), v: totals.pending },
    { key: 'lapsed', label: t('dash.unacc.lapsed'), v: totals.expired + totals.voided },
    { key: 'returned', label: t('dash.unacc.returned'), v: totals.returned },
    { key: 'unaccounted', label: t('dash.unacc.unaccounted'), v: totals.unaccounted },
  ] as const;
  return (
    <section ref={ref} className={cn('flex flex-col gap-4 rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      <div className="flex flex-wrap items-baseline gap-3.5">
        <span className="text-[52px] font-bold leading-none tracking-[-0.025em] tabular-nums" style={{ color: pending ? 'var(--color-dash-orange)' : totals.unaccounted ? 'var(--color-dash-orange)' : 'var(--color-dash-olive)' }}>
          {pending ? t('dash.pending') : totals.unaccountedRate.value == null ? '—' : `${(totals.unaccountedRate.value * p).toFixed(1)}%`}
        </span>
        <div className="flex min-w-0 flex-1 basis-[180px] flex-col gap-[3px]">
          <span className="text-[19px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{t('dash.unacc.title')}</span>
          <span className="dash-mono text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>
            {pending ? t('dash.unacc.noUnits') : t('dash.unacc.basis', { n: fmtInt(totals.unaccounted), out: fmtInt(totals.consumed) })}
          </span>
        </div>
      </div>
      <p className="text-sm leading-[1.55] [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-2)' }}>{pending ? t('dash.unacc.bodyPending') : t('dash.unacc.body')}</p>
      {!pending &&
        split.map((s) => (
          <div key={s.key} className="flex items-center gap-3 border-b py-[9px]" style={{ borderColor: 'var(--color-dash-track)' }}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: SEGMENT_COLORS[s.key] }} />
            <span className="min-w-0 flex-1 text-sm" style={{ color: 'var(--color-dash-fg-2)' }}>{s.label}</span>
            <span className="dash-mono text-base font-bold" style={{ color: 'var(--color-dash-fg)' }}>{fmtInt(Math.round(s.v * p))}</span>
          </div>
        ))}
      <span className="sr-only">{bigOf(totals.unaccountedRate)}</span>
    </section>
  );
}
